import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { Container, Row } from 'reactstrap';
import {
  Button,
  ConfirmModal,
  CustomModal,
  PageHeader,
  Section,
  SpinnerArea,
} from '../../../../shared/components';
import OperationContext from '../context';
import VersionTable from './VersionTable';
import ActionPlanContext from './ActionPlanContext';
import {
  OperActionPlan,
  OperationActionPlanCreateDto,
} from '../../../../services/types/ActionPlan';
import EmptyHistory from './EmptyHistory';
import NewActionPlan from './NewActionPlan';
import operActionPlanService from '../../../../services/operActionPlanService';
import Context from '../../../App/MainProvider/context';
import { BasicNotification } from '../../../../shared/components/Notification';
import { isDraft } from '../../../../shared/misc/utils';

const ActionPlan: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { operation } = useContext(OperationContext);
  const { operationActionPlans, canCreate, grantedWrite, loading, blokedBySR, stepSR, reload } =
    useContext(ActionPlanContext);
  const sortByVersion = (a: OperActionPlan, b: OperActionPlan) => b.version - a.version;
  const actionPlanVersions = [...operationActionPlans];
  const [showNewActionPlanModal, setShowNewActionPlanModal] = useState(false);
  const [showNewActionPlanFromCurrentModal, setShowNewActionPlanFromCurrentModal] = useState(false);

  const { service, notify } = useContext(Context);

  const lastVersion: OperActionPlan[] = actionPlanVersions.filter((x, i) => i === 0);
  const otherVersions: OperActionPlan[] = actionPlanVersions.filter((x, i) => i > 0);
  const [saveLoading, setSaveLoading] = useState(false);

  actionPlanVersions.sort(sortByVersion);

  const createActionPlan = (language: string, createFromCurrent: boolean) => {
    const callback = (actionPlanId: number) => {
      setSaveLoading(true);

      notify(t('saveActionPlanForm'), t('successSave'), 'success');
      // ActionPlanProvider envuelve tanto esta lista como la pantalla de
      // detalle en una sola instancia (OperationRoutes.tsx), así que su
      // fetch corre una única vez al montar y persiste mientras se navega
      // entre lista y detalle sin salir de /action-plan/*. Sin este reload(),
      // volver "atrás" desde el detalle recién creado mostraba la lista vieja
      // - hacía falta F5 para verlo (bug real reportado por el usuario).
      reload();
      navigate(`/operations/${operation.number}/action-plan/${actionPlanId}`);
    };
    const payload: OperationActionPlanCreateDto = {
      operationId: operation.id,
      language,
      createFromCurrent,
    };
    service(() => operActionPlanService.saveActionPlan(payload), {
      callback,
      loader: setSaveLoading,
    });
  };

  return (
    <>
      <SpinnerArea loading={loading || saveLoading}>
        <Container className="dashboard">
          <PageHeader title={t('actionPlanTitle', { number: operation.number })} />
          <Row>
            {canCreate && !isDraft(operation) ? (
              <>
                <Section>
                  <div className="card__title d-flex justify-content-between">
                    <h5>{t('currentVersion')}</h5>
                    {grantedWrite && !blokedBySR && (
                      <div className="">
                        {actionPlanVersions && actionPlanVersions.length > 0 && (
                          <Button
                            key="btnNewFromMostRecent"
                            className="btn btn-primary px-4 py-1"
                            onClick={() => {
                              setShowNewActionPlanFromCurrentModal(true);
                            }}
                            text={t('actionPlanNewFromMostRecent')}
                          />
                        )}

                        <Button
                          key="btnAdd"
                          className="btn btn-primary px-4 py-1"
                          onClick={() => {
                            setShowNewActionPlanModal(true);
                          }}
                          text={t('actionPlanNew')}
                        />
                      </div>
                    )}
                  </div>
                  <div className="last-version-content">
                    <div className="table-wrapper sticky-section w-100">
                      <VersionTable
                        operation={operation}
                        items={lastVersion}
                        className="last-version-content"
                        type="current"
                      />
                    </div>
                    {blokedBySR && (
                      <div className="notification-wrapper-100 d-flex justify-content-center mb-3">
                        <BasicNotification
                          message={t('actionPlanStepWarningMessage', { step: stepSR })}
                          color="warning"
                          icon="exclamation-triangle"
                        />
                      </div>
                    )}
                  </div>
                </Section>
                {actionPlanVersions.length > 0 && (
                  <Section title={t('history')}>
                    {otherVersions.length > 0 ? (
                      <VersionTable
                        operation={operation}
                        items={otherVersions}
                        className="history-content"
                        type="previous"
                      />
                    ) : (
                      <EmptyHistory />
                    )}
                  </Section>
                )}
              </>
            ) : (
              <Section>
                <div className="m-3 d-flex justify-content-center">
                  <h1 className="page-title inner-title">
                    {t('actionPlanCreateCondition', { number: operation.number })}
                  </h1>
                </div>
              </Section>
            )}
          </Row>
        </Container>
      </SpinnerArea>
      <CustomModal
        title={t('newActionPlanTitle')}
        visible={showNewActionPlanModal}
        setVisible={setShowNewActionPlanModal}
        customClass="modal-dialog--header-task"
      >
        <NewActionPlan handleSubmit={(language) => createActionPlan(language, false)} />
      </CustomModal>
      {lastVersion && lastVersion.length > 0 && (
        <ConfirmModal
          title={t('newActionPlanFromCurrentTitle')}
          buttonText={t('createNewActionPlanfromCurrent')}
          visible={showNewActionPlanFromCurrentModal}
          setVisible={setShowNewActionPlanFromCurrentModal}
          action={() => {
            createActionPlan(lastVersion[0].language, true);
          }}
        >
          {t('createNewActionPlanfromCurrentWarning')}
        </ConfirmModal>
      )}
    </>
  );
};

export default ActionPlan;
